import { createAppKit } from "@reown/appkit/vue";
import { mainnet } from "@reown/appkit/networks";
import { WagmiAdapter } from "@reown/appkit-adapter-wagmi";
// import { BitcoinAdapter } from "@reown/appkit-adapter-bitcoin";
// import { bitcoin } from "@reown/appkit/networks";
import { ConcordiumAdapter } from "./concordium/concordiumAdapter.js";
import { concordium } from "./concordium/concordiumNetwork.js";
import {
  CCD_NAMESPACE,
  CCD_CHAIN_ID,
  CCD_WC_METHODS,
  CCD_RPC_URL,
} from "./concordium/ccdConstants.js";
import concordiumIcon from "@/assets/img/Concordium_black_symbol.png";
import rocketfuelIcon from "@/assets/img/rocketfuel-circular.svg";
import { BitcoinAdapter } from "@reown/appkit-adapter-bitcoin";
import { resolveAppKitMetadata } from "./appkitMetadata";
import { scheduleWalletConnectRecovery } from "./concordium/walletConnectRecovery.js";
// 1. Get projectId from https://cloud.reown.com
const projectId = process.env.VUE_APP_WALLET_CONNECT_PROJECT_ID;
// 2. Metadata must use the live page origin (critical for iframe embeds + Verify API)
const metadata = resolveAppKitMetadata();

if (!projectId && typeof console !== "undefined") {
  console.warn(
    "[Reown] VUE_APP_WALLET_CONNECT_PROJECT_ID is missing — WalletConnect will not work."
  );
}
const bitcoinAdapter = new BitcoinAdapter({
  projectId,
});

// 3. Set the networks (EVM chains)
const evmNetworks = [mainnet];
// const networks = [1, 137, 2000];

// 4. Create Wagmi Adapter for EVM chains
const wagmiAdapter = new WagmiAdapter({
  networks: evmNetworks,
  projectId,
});

// 5. Create Concordium Adapter for non-EVM chain
const concordiumNetworks = [concordium];
const concordiumAdapter = new ConcordiumAdapter({
  projectId,
  networks: concordiumNetworks,
});

// 6. Configure WalletConnect to support both EVM and Concordium
// Include Concordium network in the networks array for AppKit to recognize it
const allNetworks = [...evmNetworks, concordium];

const genericChains = evmNetworks.map((network) => `generic:${network.id}`);

const genericRpcMap = evmNetworks.reduce((acc, network) => {
  const rpcUrl =
    network.rpcUrls?.default?.http?.[0] ||
    network.rpcUrls?.public?.http?.[0] ||
    network.rpcUrl;
  if (rpcUrl) {
    acc[`generic:${network.id}`] = rpcUrl;
  }
  return acc;
}, {});

const genericMethods = [
  "eth_accounts",
  "eth_requestAccounts",
  "eth_sendTransaction",
  "eth_sendRawTransaction",
  "eth_sign",
  "eth_signTransaction",
  "eth_signTypedData",
  "eth_signTypedData_v3",
  "eth_signTypedData_v4",
  "eth_chainId",
  "wallet_switchEthereumChain",
  "wallet_addEthereumChain",
  "wallet_getPermissions",
  "wallet_requestPermissions",
  "wallet_registerOnboarding",
  "wallet_watchAsset",
  "wallet_scanQRCode",
  "wallet_getCallsStatus",
  "wallet_sendCalls",
  "wallet_getCapabilities",
  "wallet_grantPermissions",
  "wallet_revokePermissions",
  "wallet_getAssets",
];

// 7. Create the modal with both adapters
// This allows the QR code to support both EVM and Concordium chains
createAppKit({
  adapters: [wagmiAdapter, concordiumAdapter, bitcoinAdapter],
  networks: allNetworks,
  projectId,
  metadata,
  showWallets: true,
  allowUnsupportedChain: false,
  themeVariables: {
    // Above iframe loaders/modals (Loader, Header, Bootstrap modals)
    "--w3m-z-index": 2147483646,
  },
  chainImages: {
    [concordium.chainId]: concordium.chainIcon || concordiumIcon,
  },
  connectorImages: {
    walletConnect: rocketfuelIcon,
  },
  // Configure universal provider to include Concordium namespace
  // This ensures Concordium wallets appear in the wallet selection modal
  universalProviderConfigOverride: {
    methods: {
      [CCD_NAMESPACE]: [
        CCD_WC_METHODS.SIGN_MESSAGE,
        CCD_WC_METHODS.SEND_TRANSACTION,
      ],
      generic: genericMethods,
    },
    chains: {
      [CCD_NAMESPACE]: [`${CCD_NAMESPACE}:${CCD_CHAIN_ID}`],
      generic: genericChains,
    },
    events: {
      [CCD_NAMESPACE]: ["chainChanged", "accountsChanged"],
      generic: ["accountsChanged", "chainChanged"],
    },
    rpcMap: {
      [`${CCD_NAMESPACE}:${CCD_CHAIN_ID}`]:
        concordium.rpcUrl || concordium.rpcUrls?.default?.http?.[0],
      ...genericRpcMap,
    },
  },
  customRpcUrls: {
    [`${CCD_NAMESPACE}:${CCD_CHAIN_ID}`]: [{ url: CCD_RPC_URL }],
    ...Object.fromEntries(
      Object.entries(genericRpcMap).map(([chainId, url]) => [
        chainId,
        [{ url }],
      ])
    ),
  },
  enableWalletGuide: false,
  features: {
    analytics: false, // Optional - defaults to your Cloud configuration,
    onramp: false,
    receive: false,
    send: false,
    socials: false,
    history: false,
    swaps: false,
    smartSessions: false,
    email: false,
  },
  themeMode: "light",
});

scheduleWalletConnectRecovery();

export { wagmiAdapter, concordiumAdapter };
